import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { probationApi } from '../../api/probation';
import { genericDocumentApi } from '../../api/generic-documents';
import { StatusBadge } from '../../components/StatusBadge';
import { CommentsThread } from '../../components/CommentsThread';
import { DocumentsSection } from '../../components/DocumentsSection';
import { RecordHeader } from '../../components/RecordHeader';
import { WorkflowTrackingTab } from '../../components/WorkflowTrackingTab';
import { PROBATION_STAGES } from '../../config/workflow-stages';
import { WorkspaceTabs } from '../../components/WorkspaceTabs';
import { usePermission } from '../../auth/AuthContext';
import { formatDate } from '../../utils/formatDate';

const STATUS_LABELS: Record<string, string> = {
  in_progress: 'In Progress', under_review: 'Awaiting HR Decision', confirmed: 'Confirmed', extended: 'Extended', non_confirmed: 'Not Confirmed',
};

export function ProbationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const recordId = Number(id);
  const canManage = usePermission('probation.manage');
  const detailQuery = useQuery({ queryKey: ['probation-detail', recordId], queryFn: () => probationApi.getById(recordId) });
  const docsQuery = useQuery({ queryKey: ['probation-documents', recordId], queryFn: () => genericDocumentApi.listFor('probation_record', recordId) });

  if (!detailQuery.data) return <div className="page"><p className="muted">Loading…</p></div>;
  const { data: record, objectives, reviews } = detailQuery.data;

  return (
    <div className="page">
      <Link to="/hr/probation">&larr; Back to Probation & Confirmation</Link>
      <RecordHeader
        title={`Probation Record - ${record.employee_name ?? ''}`}
        readOnly={!canManage}
        entityType="probation_record"
        entityId={recordId}
        fields={[
          { label: 'Start date', value: formatDate(record.start_date) },
          { label: 'Expected end date', value: formatDate(record.expected_end_date) },
          { label: 'Extended end date', value: record.extension_new_end_date ? formatDate(record.extension_new_end_date) : '-' },
          { label: 'Supervisor', value: record.supervisor_name ?? '-' },
          { label: 'Supervisor recommendation', value: record.supervisor_recommendation ?? '-' },
          { label: 'HR decision', value: record.hr_decision ?? '-' },
        ]}
      />
      <p className="muted">Status: <StatusBadge status={record.status} label={STATUS_LABELS[record.status]} /></p>

      <WorkspaceTabs
        tabs={[
          {
            key: 'details',
            label: 'Details',
            content: (
              <>
                <section className="card">
                  {record.supervisor_recommendation_comments && (<><h3>Supervisor Comments</h3><p>{record.supervisor_recommendation_comments}</p></>)}
                  {record.hr_decision_comments && (<><h3>HR Decision Comments</h3><p>{record.hr_decision_comments}</p></>)}
                  {objectives.length > 0 && (
                    <>
                      <h3>Objectives</h3>
                      <ul>{objectives.map((o) => <li key={o.id}>{o.objective}</li>)}</ul>
                    </>
                  )}
                  {reviews.length > 0 && (
                    <>
                      <h3>Reviews</h3>
                      {reviews.map((r) => (
                        <p key={r.id}><strong>{formatDate(r.review_date)}</strong> by {r.submitted_by_name} - {r.assessment}</p>
                      ))}
                    </>
                  )}
                </section>
                <CommentsThread entityType="probation_record" entityId={recordId} />
              </>
            ),
          },
          {
            key: 'documents',
            label: 'Documents',
            content: (
              <DocumentsSection
                documents={docsQuery.data?.data ?? []}
                downloadUrl={(documentId) => genericDocumentApi.downloadUrl('probation_record', documentId)}
                upload={(input) => genericDocumentApi.uploadFor('probation_record', recordId, input)}
                onDelete={(documentId) => genericDocumentApi.deleteFor('probation_record', documentId)}
                queryKeyToInvalidate={['probation-documents', recordId]}
                hrCategories={false}
              />
            ),
          },
          {
            key: 'tracking',
            label: 'Tracking',
            content: <WorkflowTrackingTab entityType="probation_record" entityId={recordId} actionStageMap={PROBATION_STAGES} />,
          },
        ]}
      />
    </div>
  );
}
